import { useEffect, useRef } from 'react';
import * as translator from '../../../../utils/translator';
import { MainTracker } from '../../../../PixelTrackers/MainTracker';
import { cn } from '../../../../utils/helpers';

interface TrustBadgeSectionProps {
  language: string;
  country: string;
  trustedShopsData?: {
    overallMark?: string;
  };
  className?: string;
}

export function TrustBadgeSection({ language, country, trustedShopsData, className }: TrustBadgeSectionProps) {
  const locale = `${language.toLowerCase()}-${country.toUpperCase()}`;
  const trustpilotRef = useRef<HTMLDivElement>(null);

  const TRUSTPILOT_COUNTRIES = ['CH', 'GB'];
  const TRUSTEDSHOPS_COUNTRIES = ['DE', 'FR'];

  useEffect(() => {
    if (TRUSTPILOT_COUNTRIES.includes(country.toUpperCase()) && trustpilotRef.current && (window as any).Trustpilot) {
      (window as any).Trustpilot.loadFromElement(trustpilotRef.current, true);
    }
  }, [country]);
  const handleTrustClick = () => {
    MainTracker.track('custom', 'Reviews Section Click', {
      source: 'trustedshop_sidebar',
    });
  };

  const StarIcon = () => (
    <svg className="w-[16px] h-[16px] md:w-[18px] md:h-[18px]" viewBox="0 0 24 24" fill="#FBBF24">
      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
    </svg>
  );

  const ETrustedBadgeIcon = () => (
    <svg className="w-[26px] h-[26px] md:w-[30px] md:h-[30px] flex-shrink-0" viewBox="0 0 216 216" fill="none" xmlns="http://www.w3.org/2000/svg">
      <g clipPath="url(#clip0_etrusted)">
        <path d="M136.164 128.498C126.302 143.077 112.584 156.364 94.1411 156.364C74.6405 156.364 63.2808 144.152 63.2808 124.428C63.2808 92.5004 86.8609 61.2007 120.079 61.2007C131.215 61.2007 146.224 65.7015 146.224 79.213C146.224 103.421 108.289 111.571 89.6403 116.287C88.9948 120.384 88.3582 124.428 88.3582 128.498C88.3582 136.863 92.8501 144.574 102.076 144.574C114.072 144.574 123.701 133.008 130.793 124.428L136.164 128.498ZM125.871 76.4246C125.871 71.4934 123.092 67.6561 117.945 67.6561C102.506 67.6561 94.5625 97.6557 91.5859 109.437C105.94 105.133 125.871 93.3521 125.871 76.4246ZM107.59 3.36333e-06C86.3095 4.02835e-06 65.507 6.31072 47.8134 18.134C30.1197 29.9573 16.3297 46.7622 8.18726 66.4232C0.0448339 86.0842 -2.08423 107.718 2.06933 128.589C6.22288 149.461 16.4725 168.631 31.5219 183.677C46.5713 198.722 65.7445 208.967 86.6166 213.116C107.489 217.264 129.122 215.13 148.781 206.982C168.44 198.835 185.242 185.041 197.061 167.344C208.88 149.647 215.185 128.843 215.18 107.563C215.18 93.4353 212.397 79.4461 206.989 66.3942C201.582 53.3423 193.657 41.4834 183.666 31.4949C173.675 21.5065 161.814 13.584 148.761 8.18002C135.707 2.77606 121.718 -0.00352943 107.59 3.36333e-06ZM106.693 175.04C93.2824 175.038 80.1732 171.06 69.0234 163.608C57.8735 156.156 49.1837 145.565 44.0528 133.174C38.9219 120.784 37.5804 107.15 40.1978 93.9969C42.8152 80.844 49.274 68.7625 58.7576 59.2802C68.2411 49.7979 80.3235 43.3407 93.4768 40.725C106.63 38.1093 120.264 39.4527 132.654 44.5852C145.043 49.7177 155.633 58.4089 163.084 69.5598C170.534 80.7106 174.511 93.8203 174.511 107.231C174.504 125.21 167.356 142.451 154.641 155.162C141.925 167.872 124.682 175.013 106.702 175.013L106.693 175.04Z" fill="black"/>
      </g>
      <defs>
        <clipPath id="clip0_etrusted">
          <rect width="215.18" height="215.18" fill="white"/>
        </clipPath>
      </defs>
    </svg>
  );

  if (TRUSTPILOT_COUNTRIES.includes(country.toUpperCase())) {
    return (
      <div id="trust-badge-section" className="flex justify-center items-center h-[60px] overflow-hidden w-full bg-[#F6F7F9] mt-8">
        {/* Fluid width so the row never runs off narrow screens. Capped at 400px
            (Trustpilot goes one-line from ~416px, pinned to the top of its frame), so
            it always renders two lines that fill the 44px frame and stay centred. */}
        <div
          ref={trustpilotRef}
          className="ie-height w-full max-w-[400px]"
          data-locale={locale}
          data-template-id="5419b6ffb0d04a076446a9af"
          data-businessunit-id="591302b60000ff0005a23087"
          data-style-height="44px"
          data-style-width="100%"
          data-theme="light"
        >
          <a 
            href="https://www.trustpilot.com/review/doktorabc.com" 
            target="_blank" 
            rel="noopener noreferrer"
          >Trustpilot</a>
        </div>
      </div>
    );
  }

  if (TRUSTEDSHOPS_COUNTRIES.includes(country.toUpperCase())) {
    return (
      <a
        id="trust-badge-section"
        href="https://www.trustedshops.de/bewertung/info_X418BD71DBE30751ABC36B56ADA98CBA1.html"
        target="_blank"
        rel="noopener noreferrer"
        className={cn("flex items-center justify-center flex-wrap gap-x-[6px] gap-y-[4px] bg-[#f6f7f9] mt-3 py-[10px] no-underline", className)}
        onClick={handleTrustClick}
      >
        <div className="flex items-center gap-[6px]">
          <ETrustedBadgeIcon />
        </div>
        <div className="flex items-center gap-[6px]">
          <div className="flex gap-[1px] flex-shrink-0">
            {[...Array(5)].map((_, i) => <StarIcon key={i} />)}
          </div>
          <span className="font-assistant font-semibold text-[14px] md:text-[15px] text-black whitespace-nowrap">
            {trustedShopsData?.overallMark || '4,81'}
          </span>
          <span className="font-assistant font-bold text-[12px] md:text-[13px] text-black whitespace-nowrap">
            {translator.translate(language, 'layout', 'sidebar/trusted_shops/very_good')}
          </span>
        </div>
      </a>
    );
  }

  return null;
}
